import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet } from "lucide-react";
import { gapi, errMsg, fmtDateTime } from "../api";
import { useGAuth } from "../GAuth";
import { PageHeader, Panel, Btn, Badge } from "../components/ui";
import { downloadCsv } from "../components/common";

const STATO = { nuovo: ["green", "Nuovo"], duplicato: ["amber", "Già presente"], doppione_file: ["amber", "Ripetuto nel file"], errore: ["red", "Errore"] };
const ARCHIVI = { contatti: "Contatti", immobili: "Immobili" };

function ExportBox() {
  const { can } = useGAuth();
  const list = [["contatti", "Contatti"], ["immobili", "Immobili"], ["locazioni", "Locazioni"]].filter(([k]) => can(k, "esportare"));
  if (!list.length) return null;
  return (
    <Panel title="Esporta archivi" testid="export-box">
      <div className="space-y-2">{list.map(([k, l]) => (
        <div key={k} className="flex items-center gap-2 text-sm"><span className="flex-1 font-semibold">{l}</span>
          <Btn variant="ghost" className="h-9" onClick={() => downloadCsv(`/export/${k}`, { formato: "xlsx" }, `${k}-casamore.xlsx`)} data-testid={`export-${k}-xlsx`}><FileSpreadsheet className="h-4 w-4" /> Excel</Btn>
          <Btn variant="ghost" className="h-9" onClick={() => downloadCsv(`/export/${k}`, { formato: "csv" }, `${k}-casamore.csv`)} data-testid={`export-${k}-csv`}>CSV</Btn></div>))}</div>
      <p className="text-xs text-anthracite/50 mt-3">Ogni esportazione viene registrata nel registro attività.</p>
    </Panel>
  );
}

function Preview({ job, onDone }) {
  const [mode, setMode] = useState("salta");
  const [busy, setBusy] = useState(false);
  const C = job.conteggi;
  const commit = async () => {
    setBusy(true);
    try { const { data } = await gapi.post(`/import/${job.id}/commit`, { duplicati: mode }); toast.success(`Importazione completata: ${data.esito.creati} creati, ${data.esito.aggiornati} aggiornati`); onDone(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title={`Anteprima · ${job.filename}`} testid="import-preview">
      <div className="flex flex-wrap gap-2 mb-3 text-sm" data-testid="import-counts">
        {Object.entries(STATO).map(([k, [t, l]]) => <Badge key={k} tone={t} testid={`import-count-${k}`}>{l}: {C[k]}</Badge>)}
      </div>
      <p className="text-xs text-anthracite/55 mb-1">Colonne riconosciute: {Object.keys(job.colonne).join(", ")}</p>
      {job.ignorate.length > 0 && <p className="text-xs text-amber-800 mb-3">Colonne ignorate: {job.ignorate.join(", ")}</p>}
      <div className="max-h-80 overflow-auto rounded-xl border border-border-subtle mb-4">
        <table className="w-full text-sm"><thead className="bg-accent-subtle/60 text-xs text-left sticky top-0"><tr><th className="p-2">Riga</th><th className="p-2">Dato</th><th className="p-2">Esito</th></tr></thead>
          <tbody>{job.righe.map((r) => (
            <tr key={r.riga} className="border-t border-border-subtle" data-testid="import-row"><td className="p-2 text-xs">{r.riga}</td>
              <td className="p-2"><b>{r.etichetta || "—"}</b><br /><span className="text-xs text-anthracite/50">{r.dettaglio}</span></td>
              <td className="p-2"><Badge tone={STATO[r.stato][0]}>{STATO[r.stato][1]}</Badge><p className="text-xs text-anthracite/55 mt-1">{r.motivi.join("; ")}</p></td></tr>))}</tbody></table>
      </div>
      {job.stato === "anteprima" ? (
        <div className="space-y-3">
          <p className="text-sm font-semibold">Cosa fare con i record già presenti?</p>
          {[["salta", "Saltali (consigliato)"], ["aggiorna", "Integra i dati mancanti nel record esistente"], ["importa", "Creali comunque come nuovi"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><input type="radio" name="dup" checked={mode === k} onChange={() => setMode(k)} data-testid={`import-mode-${k}`} /> {l}</label>))}
          <p className="text-xs text-anthracite/50">Le righe con errori non vengono mai importate. Gli immobili importati entrano come bozza e non sono pubblicati sul sito.</p>
          <Btn onClick={commit} disabled={busy} data-testid="import-commit">{busy ? "Importazione…" : "Conferma importazione"}</Btn>
        </div>
      ) : <p className="text-sm" data-testid="import-result">Esito: {job.esito.creati} creati · {job.esito.aggiornati} aggiornati · {job.esito.saltati} saltati · {job.esito.errori} con errori</p>}
    </Panel>
  );
}

export default function Importazioni() {
  const { can } = useGAuth();
  const [entita, setEntita] = useState("contatti");
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState([]);
  const loadHist = () => gapi.get("/import/history").then((r) => setHist(r.data)).catch(() => {});
  useEffect(() => { loadHist(); }, []);
  const preview = async () => {
    if (!file) return toast.error("Seleziona un file .csv o .xlsx");
    const fd = new FormData(); fd.append("entita", entita); fd.append("file", file);
    setBusy(true);
    try { setJob((await gapi.post("/import/preview", fd)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="g-import-page">
      <PageHeader eyebrow="Dati" title="Importazioni" desc="Carica contatti o immobili da CSV o Excel: prima vedi l'anteprima con i doppioni, poi decidi cosa importare." />
      <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
        <div className="space-y-4">
          {can("importazioni", "creare") && (
            <Panel title="Nuova importazione" testid="import-form">
              <div className="flex flex-wrap gap-2 mb-4">{Object.entries(ARCHIVI).map(([k, l]) => (
                <button key={k} onClick={() => { setEntita(k); setJob(null); }} className={`rounded-full px-4 h-9 text-sm font-semibold border ${entita === k ? "bg-brand text-white border-brand" : "bg-white border-border-subtle"}`} data-testid={`import-entity-${k}`}>{l}</button>))}</div>
              <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border-subtle p-8 cursor-pointer hover:bg-accent-subtle/40 text-sm text-center" data-testid="import-dropzone">
                <Upload className="h-6 w-6 text-brand" />{file ? <b>{file.name}</b> : "Scegli un file .csv o .xlsx (max 5.000 righe)"}
                <input type="file" accept=".csv,.xlsx,.txt" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setJob(null); }} data-testid="import-file" />
              </label>
              <div className="flex flex-wrap gap-2 mt-4">
                <Btn onClick={preview} disabled={busy} data-testid="import-preview-btn">{busy ? "Analisi…" : "Analizza file"}</Btn>
                <Btn variant="ghost" onClick={() => downloadCsv(`/import/template/${entita}`, { formato: "xlsx" }, `modello-${entita}.xlsx`)} data-testid="import-template-xlsx"><Download className="h-4 w-4" /> Modello Excel</Btn>
                <Btn variant="ghost" onClick={() => downloadCsv(`/import/template/${entita}`, { formato: "csv" }, `modello-${entita}.csv`)} data-testid="import-template-csv">Modello CSV</Btn>
              </div>
            </Panel>
          )}
          {job && <Preview job={job} onDone={(j) => { setJob(j); loadHist(); }} />}
        </div>
        <div className="space-y-4">
          <ExportBox />
          <Panel title="Ultime importazioni" testid="import-history">
            <ul className="space-y-2 text-sm">{hist.map((h) => (
              <li key={h.id}><b>{h.filename}</b> <span className="text-xs text-anthracite/50">· {ARCHIVI[h.entita]} · {fmtDateTime(h.createdAt)} · {h.userNome}</span><br />
                <span className="text-xs">{h.esito ? `${h.esito.creati} creati, ${h.esito.aggiornati} aggiornati, ${h.esito.saltati} saltati, ${h.esito.errori} errori` : "anteprima non confermata"}</span></li>))}
              {!hist.length && <li className="text-anthracite/45">Nessuna importazione.</li>}</ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}
